import { renderElement } from '../dom';
import { buildEventEmitter, EventEmitter } from '../events';
import { getTime, onNextFrame } from '../timers';

type CountdownClock = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const msPerSecond = 1000;
const msPerMinute = 60 * msPerSecond;
const msPerHour = 60 * msPerMinute;
const msPerDay = 24 * msPerHour;

const clockScales: [keyof CountdownClock, number][] = [
  ['days', msPerDay],
  ['hours', msPerHour],
  ['minutes', msPerMinute],
  ['seconds', msPerSecond],
];

export const buildCountdownClock = (
  totalTimeRemaining: number,
): CountdownClock =>
  clockScales.reduce(
    ({ clock, timeRemaining }, [key, scale]) => ({
      clock: {
        ...clock,
        [key]: Math.max(0, Math.floor(timeRemaining / scale)),
      },
      timeRemaining: Math.max(0, timeRemaining % scale),
    }),
    {
      clock: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      },
      timeRemaining: totalTimeRemaining,
    },
  ).clock;

const buildCountdownClockAnimation = (endTime: number) => {
  const emitter = buildEventEmitter<CountdownClock>(['end', 'start', 'update']);

  let started = false;
  const step = () => {
    const currentTime = getTime();
    const timeRemaining = Math.max(endTime - currentTime, 0);
    const clock = buildCountdownClock(timeRemaining);

    if (!started) {
      started = true;

      emitter.emit('start', clock);
    }

    emitter.emit('update', clock);

    if (timeRemaining === 0) {
      emitter.emit('end', clock);
    } else {
      onNextFrame(step);
    }
  };

  onNextFrame(step);

  return emitter;
};

export const animateCountdownClock = (endDate: any) => {
  if (typeof endDate !== 'object' || !(endDate instanceof Date)) {
    throw new Error(
      'FLMPiano.animateCountdownClock expected the first parameter to be a Date',
    );
  }

  const endTime = endDate.getTime();
  if (isNaN(endTime)) {
    throw new Error(
      'FLMPiano.animateCountdownClock expected the first parameter to be a valid Date',
    );
  }

  return buildCountdownClockAnimation(endTime);
};

export const animateCountdownClockElement = (selector: any, endDate: any) => {
  if (typeof selector !== 'string') {
    throw new Error(
      'FLMPiano.animateElementCountdownClock expected an element selector',
    );
  }
  const element = document.querySelector(selector);
  if (!element) {
    throw new Error(
      `FLMPiano.animateElementCountdownClock could not find an element matching the selector '${selector}'`,
    );
  }

  const animation = animateCountdownClock(endDate);

  applyCountdownClockAnimationToElement(element, animation);

  return animation;
};

const applyCountdownClockAnimationToElement = (
  element: Element,
  animation: EventEmitter<CountdownClock>,
) => {
  const daysElement = element.querySelector('.days');
  if (!daysElement) {
    throw new Error(
      'Could not find a .days element inside of the provided clock element',
    );
  }

  const hoursElement = element.querySelector('.hours');
  if (!hoursElement) {
    throw new Error(
      'Could not find a .hours element inside of the provided clock element',
    );
  }

  const minutesElement = element.querySelector('.minutes');
  if (!minutesElement) {
    throw new Error(
      'Could not find a .minutes element inside of the provided clock element',
    );
  }

  const secondsElement = element.querySelector('.seconds');
  if (!secondsElement) {
    throw new Error(
      'Could not find a .seconds element inside of the provided clock element',
    );
  }

  animation.addEventListener('*', ({ days, hours, minutes, seconds }) => {
    daysElement.innerHTML = String(days).padStart(2, '0');
    hoursElement.innerHTML = String(hours).padStart(2, '0');
    minutesElement.innerHTML = String(minutes).padStart(2, '0');
    secondsElement.innerHTML = String(seconds).padStart(2, '0');
  });
};

export const renderCountdownClock = (endDate: any) => {
  const element = renderElement(
    'div',
    {},
    renderElement('div', { className: 'days' }, '00'),
    renderElement('div', { className: 'minutes' }, '00'),
    renderElement('div', { className: 'hours' }, '00'),
    renderElement('div', { className: 'seconds' }, '00'),
  );

  const animation = animateCountdownClock(endDate);

  applyCountdownClockAnimationToElement(element, animation);

  return {
    animation,
    element,
  };
};
